import {PropsWithChildren, useEffect, useRef, useState} from 'react';
import {ApolloError} from '@apollo/client';
import {isString} from 'lodash';
import {useCookie} from 'react-use';

import {NoPermissionError} from 'core/utils/errors';
import {dayjs} from 'utils/dateUtils';
import {setFeatureFlags, setUserInfo} from 'core/stores/auth';
import {getMyInfo, MyInfo} from 'core/api/userInfo';
import {setUserTimezone} from 'core/api/timezone';
import {fetchFeatureFlags} from 'core/api/featureFlags';
import {checkFeatureFlag} from 'core/hooks/authHooks';
import {FeatureFlags} from 'core/constants/featureFlags';
import {impersonationToken} from 'core/impersonation';

import {LoadingScreen} from 'components/LoadingScreen';

const TENANT_ID_KEY = 'tenantId';

const getParentDomain = () => {
  const parts = window.location.hostname.split('.');

  if (parts.length === 3) {
    return `.${parts[1]}.${parts[2]}`;
  }

  return undefined;
};

export enum AuthorizeErrorType {
  FlagsError = 'FLAGS_ERROR',
  UserInfoError = 'USER_INFO_ERROR',
  NoPermissionError = 'NO_PERMISSION_ERROR',
}

export const UserInfoProvider = ({children}: PropsWithChildren) => {
  const setUserTimezoneCalledRef = useRef(false);
  const [errorType, setErrorType] = useState<AuthorizeErrorType | null>(null);
  const [loading, setLoading] = useState(true);
  const [, updateTenantIdCookie] = useCookie(TENANT_ID_KEY);

  useEffect(() => {
    const loadData = async () => {
      try {
        const featureFlags = await fetchFeatureFlags();
        setFeatureFlags(featureFlags);
      } catch {
        setErrorType(AuthorizeErrorType.FlagsError);
        return;
      }

      let myInfo: MyInfo;

      try {
        myInfo = await getMyInfo();

        if (
          checkFeatureFlag(FeatureFlags.UserManagement, myInfo.userInfo.tenantId) &&
          !myInfo.userInfo.role?.appPermissions?.length
        ) {
          setErrorType(AuthorizeErrorType.NoPermissionError);
          return;
        }
      } catch (error) {
        const isForbiddenError =
          error instanceof ApolloError &&
          error?.networkError &&
          'result' in error.networkError &&
          !isString(error.networkError.result) &&
          error.networkError.result?.errors?.[0]?.extensions?.code === 'FORBIDDEN';

        setErrorType(isForbiddenError ? AuthorizeErrorType.NoPermissionError : AuthorizeErrorType.UserInfoError);
        return;
      }

      const userInfo = myInfo.userInfo;
      const profile = userInfo.profile;
      const userTimezone = dayjs.tz.guess();

      if (userTimezone !== userInfo.timeZone && !setUserTimezoneCalledRef.current) {
        setUserTimezoneCalledRef.current = true;
        await setUserTimezone(userTimezone);
      }

      setUserInfo({
        id: userInfo.id,
        ennablEmployeeId: userInfo.ennablEmployeeId || null,
        permissions: userInfo.permissions.map(permission => permission.id),
        roles: userInfo.roles.map(role => role.id),
        roleType: userInfo.roleType,
        tenantId: userInfo.tenantId,
        tenantName: userInfo.tenantName,
        timeZone: userTimezone,
        role: userInfo.role,
        profile: profile
          ? {
              email: profile.email || undefined,
              familyName: profile.familyName || undefined,
              givenName: profile.givenName || undefined,
              name: profile.name || undefined,
            }
          : undefined,
      });

      if (!impersonationToken) {
        updateTenantIdCookie(userInfo.tenantId, {
          domain: getParentDomain(),
          path: '/',
          secure: true,
          sameSite: 'lax',
          expires: 400,
        });
      }
    };

    loadData().finally(() => setLoading(false));
  }, [updateTenantIdCookie]);

  if (errorType && errorType === AuthorizeErrorType.NoPermissionError) {
    throw new NoPermissionError();
  }

  if (errorType) {
    throw new Error(`Error while authorizing: ${errorType}`);
  }

  if (loading) {
    return <LoadingScreen />;
  }

  return children;
};
